import React, { SelectHTMLAttributes, useCallback, useMemo } from 'react';
import baseStyles from '../form-field-base.module.css';
import SearchableSelect from '../SearchableSelect/SearchableSelect';
import styles from './FormSelect.module.css';

export interface FormSelectOption {
  value: string;
  label: string;
  group?: string;
}

interface FormSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  required?: boolean;
  options: readonly FormSelectOption[] | FormSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  containerClassName?: string;
  readOnly?: boolean;
  loading?: boolean;
  triggerFormat?: (option: FormSelectOption) => React.ReactNode;
  optionFormat?: (option: FormSelectOption) => React.ReactNode;
  allowEmpty?: boolean;
  onSearchChange?: (query: string) => void;
}

const FormSelect: React.FC<FormSelectProps> = ({
  label,
  error,
  required: isRequired,
  options,
  placeholder = 'Selecione...',
  searchPlaceholder,
  id,
  containerClassName = '',
  className = '',
  readOnly = false,
  loading = false,
  triggerFormat,
  optionFormat,
  allowEmpty,
  onSearchChange,
  onChange,
  disabled,
  name,
  value,
  ...rest
}) => {
  const searchableOptions = useMemo(
    () => options.map((option) => ({ value: option.value, label: option.label, group: option.group })),
    [options]
  );

  const triggerCls = [
    styles.searchableTrigger,
    readOnly ? styles.searchableTriggerReadOnly : '',
    className,
  ].filter(Boolean).join(' ');

  const handleValueChange = useCallback((nextValue: string) => {
    if (!onChange) {
      return;
    }

    const syntheticEvent = {
      target: {
        value: nextValue,
        name: name ?? '',
        id: id ?? '',
      },
      currentTarget: {
        value: nextValue,
        name: name ?? '',
        id: id ?? '',
      },
    } as React.ChangeEvent<HTMLSelectElement>;

    onChange(syntheticEvent);
  }, [id, name, onChange]);

  return (
    <div className={`${baseStyles.fieldGroup} ${containerClassName}`}>
      {label && (
        <label htmlFor={id} className={baseStyles.label}>
          {label}
          {isRequired && <span className={baseStyles.required}>*</span>}
        </label>
      )}
      <SearchableSelect
        value={typeof value === 'string' ? value : ''}
        onChange={handleValueChange}
        options={searchableOptions}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        disabled={disabled}
        loading={loading}
        hasError={Boolean(error)}
        readOnly={readOnly}
        triggerFormat={triggerFormat}
        optionFormat={optionFormat}
        allowEmpty={allowEmpty}
        onSearchChange={onSearchChange}
        className={triggerCls}
      />
      {error && <span className={baseStyles.errorText}>{error}</span>}
    </div>
  );
};

export default FormSelect;
